import * as React from 'react'
import Modal from '@src/components/common/dialog' // Footer // ModalRow,
import RadioButton from '@src/components/common/radio.button'
import Checkbox from '@src/components/common/checkbox'
import { isEmpty } from 'lodash'
import cx from 'classnames'
import MultipleSelectChip, {
  SelectOption
} from '@src/components/common/material/multiple.select.chip'
import { GuardrailEditModel, SeverityEnum } from '../../models'
import Button from '@src/components/common/button'
import editorStyles from './editor.guardrail.styl'
import TextBox from '@src/components/common/text.box'

interface ModalRowProps {
  className?: string
  classNameInput?: string
  label?: string
  labelProps?: {
    className?: string
  }
  inputProps?: {
    className?: string
  }
}

export const ModalRow: React.FC<ModalRowProps> = ({
  label = '',
  className,
  labelProps = {},
  inputProps = {},
  children
}: React.PropsWithChildren<ModalRowProps>) => (
  <div className={cx(className, editorStyles.wrapperRow)}>
    <label
      className={cx(editorStyles.label, labelProps?.className)}
    >{`${label}${!isEmpty(label) ? ':' : ''}`}</label>
    <div className={cx(inputProps?.className, editorStyles.wrapperInput)}>
      {children}
    </div>
  </div>
)

export interface EditorGuardrailProps {
  maxWidthContent?: React.ReactText
  readonlyFields?: boolean
  useMoreFields?: boolean
  showQueryField?: boolean
  type?: 'label' | 'folder'
  multipleFolders?: boolean
  title?: string
  titleSave?: string
  contentText?: string
  open: boolean
  onPost?: (model?: GuardrailEditModel, categories?: string[]) => void
  onClose: () => void
  data: GuardrailEditModel
  guardrailCategories?: SelectOption<string>[]
}

type ReqFields = 'name' | 'query' | 'remediation' | 'audit'
interface EditorGuardrailState extends GuardrailEditModel {
  formValidation: {
    isValid: boolean
    messages?: { [value in ReqFields]?: string }
  }
}

export const defaultState: EditorGuardrailState = {
  name: '',
  severity: null,
  query: '',
  id: null,
  label: '',
  description: '',
  suppressionDeleteObject: true,
  suppressionCreateObject: false,
  suppressionDeleteIdentity: true,
  suppressionCreateIdentity: false,
  categories: [],
  formValidation: {
    isValid: false
  },
  remediation: '',
  audit: ''
}

const EditorGuardrailView: React.FC<EditorGuardrailProps> = React.memo(
  (props: EditorGuardrailProps) => {
    const {
      maxWidthContent,
      title,
      titleSave,
      onClose,
      onPost,
      contentText,
      data,
      guardrailCategories,
      open,
      useMoreFields,
      readonlyFields,
      showQueryField,
      type,
      multipleFolders
    } = props

    const [state, setState] = React.useState<EditorGuardrailState>(defaultState)

    const [showMore, toggleMore] = React.useState(false)

    function onOpenModal(data: GuardrailEditModel = {} as GuardrailEditModel) {
      const newState = { ...defaultState, ...data }
      setState(prevState => ({
        ...newState,
        formValidation: validateForm({
          name: data.name,
          query: data.query
        })
      }))
    }

    React.useEffect(() => {
      const { data, open } = props
      if (open) {
        onOpenModal(data)
      }
      if (!isEmpty(data?.audit) || !isEmpty(data?.remediation)) {
        toggleMore(true)
      } else {
        toggleMore(false)
      }
    }, [open])

    const handleChange =
      (
        property:
          | 'suppressionDeleteObject'
          | 'suppressionCreateObject'
          | 'suppressionDeleteIdentity'
          | 'suppressionCreateIdentity'
      ) =>
      (
        event: React.ChangeEvent<HTMLInputElement> &
          React.KeyboardEvent<HTMLInputElement>
      ) => {
        setState(prevState => ({
          ...prevState,
          [property]: !prevState[property]
        }))
      }

    function onTextChange(
      value: React.ReactText,
      key: keyof EditorGuardrailState
    ) {
      const formValidation = validateForm({
        name: state.name,
        query: state.query,
        [key]: value
      })
      setState(prevState => ({
        ...prevState,
        [key]: value,
        formValidation
      }))
    }

    function handleSeverityChange(
      changeEvent: React.ChangeEvent<HTMLInputElement>
    ) {
      changeEvent.persist()
      setState(prevState => ({
        ...prevState,
        severity: changeEvent?.target?.value as SeverityEnum
      }))
    }

    function handlePost() {
      if (typeof onPost === 'function') {
        onPost({ ...state }, state?.categories)
      }
    }

    function handleClose() {
      if (typeof onClose === 'function') {
        onClose()
      }
    }

    function onChangeMultipleSelectCategory(value: string[]) {
      setState(prevState => ({ ...prevState, categories: value }))
    }

    function onChangeAudit(event: React.ChangeEvent<HTMLTextAreaElement>) {
      const text = event.target.value || ''
      onTextChange(text, 'audit')
    }

    function onChangeRemediation(
      event: React.ChangeEvent<HTMLTextAreaElement>
    ) {
      const text = event.target.value || ''
      onTextChange(text, 'remediation')
    }

    function onToggleMore() {
      toggleMore(prev => !prev)
    }

    function validateForm(fields: { [value in ReqFields]?: string }): {
      isValid: boolean
      messages?: { [value in ReqFields]?: string }
    } {
      const messages: { [value in ReqFields]?: string } = {}
      if (readonlyFields) {
        return { isValid: true }
      }

      if (isEmpty(fields?.name) || fields?.name?.length < 3) {
        messages.name = 'Name should be at least 3 chars'
      }
      if (isEmpty(fields?.query) || fields?.query?.length < 3) {
        messages.query = 'Query should be at least 3 chars'
      }
      if (
        !isEmpty(fields?.remediation) &&
        fields?.remediation?.length > 10000
      ) {
        messages.remediation =
          'Remediation must be less than 10 000 characters.'
      }
      if (!isEmpty(fields?.audit) && fields?.audit?.length > 10000) {
        messages.audit = 'Audit must be less than 10 000 characters.'
      }

      return {
        isValid: isEmpty(messages),
        messages: isEmpty(messages) ? undefined : messages
      }
    }

    const { id } = data
    const isNew = isEmpty(id)

    return (
      <Modal
        maxWidthContent={maxWidthContent}
        title={title || 'GUARDRAIL QUERY'}
        open={props?.open}
        onClose={props?.onClose}
        footer={
          <div className={editorStyles.footer}>
            <>
              <Button
                style={'secondary'}
                icon={null}
                className={editorStyles.cancelQueryButton}
                onClick={handleClose}
                title={'CANCEL'}
              />
              <Button
                style={'primary'}
                icon={null}
                className={editorStyles.createQueryButton}
                onClick={handlePost}
                title={titleSave || `${isNew ? 'CREATE' : 'UPDATE'}`}
                disabled={!readonlyFields && !state.formValidation?.isValid}
              />
            </>
          </div>
        }
        contentText={contentText}
      >
        <>
          <div className={cx(editorStyles.inputsBox)}>
            <ModalRow
              labelProps={{
                className: cx({
                  isDisabled: readonlyFields
                })
              }}
              label="Guardrail Name"
            >
              <TextBox
                readOnly={readonlyFields}
                className={cx(editorStyles.textBox, {
                  [editorStyles.readonly]: readonlyFields
                })}
                value={state?.name}
                type="text"
                name="name"
                onChange={onTextChange}
                placeholder="Unique Guardrail Name..."
              />
              <p className={editorStyles.validationLabel}>
                {state.formValidation?.messages?.name}
              </p>
            </ModalRow>
            <ModalRow
              labelProps={{
                className: cx({
                  isDisabled: readonlyFields
                })
              }}
              label="Description"
            >
              <TextBox
                readOnly={readonlyFields}
                className={cx(editorStyles.textBox, {
                  [editorStyles.readonly]: readonlyFields
                })}
                value={state?.description}
                type="text"
                name="description"
                onChange={onTextChange}
                placeholder="Description..."
              />
            </ModalRow>
            {(showQueryField || !isNew) && (
              <ModalRow
                labelProps={{
                  className: cx({
                    isDisabled: readonlyFields
                  })
                }}
                label="Query"
              >
                <TextBox
                  readOnly={readonlyFields}
                  className={cx(editorStyles.textBox, {
                    [editorStyles.readonly]: readonlyFields
                  })}
                  value={state?.query}
                  type="text"
                  name="query"
                  onChange={onTextChange}
                  placeholder="Query..."
                />
                <p className={editorStyles.validationLabel}>
                  {state.formValidation?.messages?.query}
                </p>
              </ModalRow>
            )}
            <ModalRow
              className={cx(editorStyles.severityButtons, {
                isDisabled: readonlyFields
              })}
              label="Alert Severity"
            >
              <RadioButton
                name="severity"
                onChange={handleSeverityChange}
                value={SeverityEnum.HIGH}
                checked={state?.severity === SeverityEnum.HIGH}
                label={`High`}
              />
              <RadioButton
                name="severity"
                onChange={handleSeverityChange}
                value={SeverityEnum.MEDIUM}
                checked={state?.severity === SeverityEnum.MEDIUM}
                label={`Medium`}
              />
              <RadioButton
                name="severity"
                onChange={handleSeverityChange}
                value={SeverityEnum.LOW}
                checked={state?.severity === SeverityEnum.LOW}
                label={`Low`}
              />
            </ModalRow>
            <ModalRow
              className={cx({
                isDisabled: readonlyFields
              })}
              label="Ignore Delete"
            >
              <Checkbox
                name="suppressionDelete"
                onChange={handleChange('suppressionDeleteIdentity')}
                value={state?.suppressionDeleteIdentity}
                label={`Identity`}
              />
              <Checkbox
                name="suppressionDelete"
                onChange={handleChange('suppressionDeleteObject')}
                value={state?.suppressionDeleteObject}
                label={`Object`}
              />
            </ModalRow>
            <ModalRow
              className={cx({
                isDisabled: readonlyFields
              })}
              label="Ignore Create"
            >
              <Checkbox
                name="suppressionDelete"
                onChange={handleChange('suppressionCreateIdentity')}
                value={state?.suppressionCreateIdentity}
                label={`Identity`}
              />
              <Checkbox
                name="suppressionDelete"
                onChange={handleChange('suppressionCreateObject')}
                value={state?.suppressionCreateObject}
                label={`Object`}
              />
            </ModalRow>
          </div>
          {useMoreFields && (
            <>
              <ModalRow label="">
                <Button
                  style={'secondary'}
                  icon={!showMore ? 'plus-square' : 'minus-square'}
                  className={editorStyles.toogleMoreButton}
                  onClick={onToggleMore}
                  title={!showMore ? `Show more` : `Hide more`}
                />
              </ModalRow>
              {showMore && (
                <div className={cx(editorStyles.inputsBox)}>
                  <ModalRow
                    labelProps={{
                      className: cx({
                        isDisabled: readonlyFields
                      })
                    }}
                    label={'Remediation'}
                  >
                    <div className={editorStyles.textareaInput}>
                      <textarea
                        className={cx({
                          [editorStyles.readonly]: readonlyFields
                        })}
                        role={'remediation_text'}
                        placeholder="Remediation..."
                        value={state?.remediation}
                        onChange={onChangeRemediation}
                        readOnly={readonlyFields}
                      />
                      <p className={editorStyles.validationLabel}>
                        {state.formValidation?.messages?.remediation}
                      </p>
                    </div>
                  </ModalRow>
                  <ModalRow
                    labelProps={{
                      className: cx({
                        isDisabled: readonlyFields
                      })
                    }}
                    label={'Audit'}
                  >
                    <div className={editorStyles.textareaInput}>
                      <textarea
                        className={cx({
                          [editorStyles.readonly]: readonlyFields
                        })}
                        role={'audit_text'}
                        placeholder="Audit..."
                        value={state?.audit}
                        onChange={onChangeAudit}
                        readOnly={readonlyFields}
                      />
                      <p className={editorStyles.validationLabel}>
                        {state.formValidation?.messages?.audit}
                      </p>
                    </div>
                  </ModalRow>
                </div>
              )}
            </>
          )}
          <ModalRow
            inputProps={{
              className: editorStyles.multipleSelectCategory
            }}
            label="Category"
          >
            {isNew ? (
              type === 'label' ? (
                <TextBox
                  className={editorStyles.textBox}
                  value={state?.label}
                  type="text"
                  name="label"
                  onChange={onTextChange}
                  placeholder="Enter categories by comma..."
                />
              ) : (
                <MultipleSelectChip
                  role={`category_guardrail`}
                  multiple={multipleFolders}
                  classNameItem={editorStyles.categorySelectItem}
                  className={editorStyles.multipleSelectChip}
                  onChange={onChangeMultipleSelectCategory}
                  value={state?.categories}
                  options={guardrailCategories}
                  displayEmpty
                  placeholder={'Select Category...'}
                />
              )
            ) : (
              <MultipleSelectChip
                role={`category_guardrail`}
                multiple={multipleFolders}
                classNameItem={editorStyles.categorySelectItem}
                className={editorStyles.multipleSelectChip}
                onChange={onChangeMultipleSelectCategory}
                value={state?.categories}
                options={guardrailCategories}
                displayEmpty
                placeholder={'Select Category...'}
              />
            )}
          </ModalRow>
        </>
      </Modal>
    )
  }
)

EditorGuardrailView.defaultProps = {
  multipleFolders: false,
  type: 'label'
}

export default EditorGuardrailView
